import React, { memo } from 'react'
import {
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ResponsiveDialog from 'components/ResponsiveDialog'
import Button from 'components/Button'
import TextField from 'components/TextField'
import TextArea from 'components/TextArea'
import FormSubmitButton from 'components/FormSubmitButton'
import AddIcon from '@mui/icons-material/Add'
import { useForm, useFormValidator, useMutationBasedFormSubmitHandlers } from 'hooks/form'
import { graphql } from 'react-relay'
import { FormErrors } from 'types'
import { SubmitQuestionDialog_Mutation } from '__generated__/SubmitQuestionDialog_Mutation.graphql'
import { FormProvider } from 'react-hook-form'

interface ISubmitQuestionDialogProps {
  open: boolean
  onClose: () => void
}

interface FormValues {
  title: string
  description: string
}

function validate(values: FormValues) {
  const errors: FormErrors<FormValues> = {}

  if (!values.title) {
    errors.title = 'Required'
  } else if (values.title.length < 10) {
    errors.title = 'Too short'
  }

  return errors
}

function SubmitQuestionDialog(props: ISubmitQuestionDialogProps) {
  const { open, onClose } = props
  const theme = useTheme()
  const form = useForm<FormValues, SubmitQuestionDialog_Mutation>({
    resolver: useFormValidator(validate),
    submitHandlers: useMutationBasedFormSubmitHandlers(
      graphql`
        mutation SubmitQuestionDialog_Mutation($input: CreateQuestionInput!) {
          createQuestion(input: $input) {
            question {
              id
            }
          }
        }
      `,
      values => ({
        input: {
          title: values.title,
          description: values.description,
        },
      }),
      {
        verifySuccess(result) {
          return Boolean(result?.createQuestion?.question?.id)
        },
        successMessage: 'Question Submitted',
        onSuccess() {
          onClose()
        },
      },
    ),
  })

  return (
    <ResponsiveDialog open={open} onClose={onClose}>
      <FormProvider {...form}>
        <form onSubmit={form.submit}>
          <DialogTitle>Submit a Question</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Have a great and relevant interview question? <br />
              Submit it to help the world hire better!
            </DialogContentText>
          </DialogContent>
          <DialogContent>
            <TextField
              {...form.useFormField('title', 'typing')}
              placeholder="What's your question?"
              fullWidth
              autoFocus
              style={{ marginBottom: theme.spacing(3) }}
            />
            <TextArea
              {...form.useFormField('description', 'typing')}
              placeholder="What should interviewers look for in answers?"
              fullWidth
              minRows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={close} data-cy="close-dialog">Close</Button>
            <FormSubmitButton
              color="primary"
              data-cy="submit-dialog"
              icon={<AddIcon />}
              label="Submit"
            />
          </DialogActions>
        </form>
      </FormProvider>

    </ResponsiveDialog>
  )
}

export default memo(SubmitQuestionDialog)
